import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { usersApi } from '@/api/endpoints'
import type { User } from '@/api/types'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { PasswordRules } from '@/components/ui/PasswordRules'
import { useToast } from '@/components/ui/Toast'
import { isPasswordValid, PASSWORD_MIN_LENGTH } from '@/lib/password'

/** Generuje czytelne hasło startowe spełniające politykę backendu. */
function generatePassword(): string {
  const lower = 'abcdefghijkmnopqrstuvwxyz'
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const digits = '23456789'
  const alphabet = lower + upper + digits
  const length = Math.max(PASSWORD_MIN_LENGTH + 2, 12)

  const randomFrom = (chars: string) => {
    const buffer = new Uint32Array(1)
    crypto.getRandomValues(buffer)
    return chars[buffer[0] % chars.length]
  }

  // Po jednym znaku z każdej klasy gwarantuje zgodność z polityką haseł.
  const required = [randomFrom(lower), randomFrom(upper), randomFrom(digits)]
  const rest = Array.from({ length: length - required.length }, () => randomFrom(alphabet))
  const all = [...required, ...rest]

  // Tasowanie Fishera–Yatesa, żeby znaki wymagane nie stały zawsze na początku.
  for (let i = all.length - 1; i > 0; i -= 1) {
    const buffer = new Uint32Array(1)
    crypto.getRandomValues(buffer)
    const j = buffer[0] % (i + 1)
    ;[all[i], all[j]] = [all[j], all[i]]
  }
  return all.join('')
}

interface Props {
  open: boolean
  onClose: () => void
  user: User | null
}

export function ResetPasswordModal({ open, onClose, user }: Props) {
  const { notifySuccess } = useToast()
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setPassword(generatePassword())
      setError(null)
    }
  }, [open])

  const mutation = useMutation({
    mutationFn: () => usersApi.resetPassword(user!.id, password, true),
    onSuccess: () => {
      notifySuccess('Hasło zostało zresetowane, a sesje użytkownika unieważnione.')
      onClose()
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiError ? caught.message : 'Nie udało się zresetować hasła.')
    },
  })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    mutation.mutate()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reset hasła"
      description={user ? `Konto: ${user.full_name} (${user.email})` : ''}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            type="submit"
            form="reset-password-form"
            isLoading={mutation.isPending}
            disabled={!isPasswordValid(password)}
          >
            Zresetuj hasło
          </Button>
        </>
      }
    >
      <form id="reset-password-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
        {error && <Alert tone="error">{error}</Alert>}

        <TextField
          label="Nowe hasło startowe"
          type="text"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          hint="Wygenerowane automatycznie — możesz je zmienić."
          autoComplete="off"
          required
        />
        <PasswordRules password={password} />

        <Button variant="outline" size="sm" onClick={() => setPassword(generatePassword())}>
          Wygeneruj inne
        </Button>

        <Alert tone="warning" title="Zapisz hasło przed zamknięciem">
          Nie zobaczysz go ponownie. Przekaż je użytkownikowi bezpiecznym kanałem — przy pierwszym
          logowaniu system wymusi zmianę. Wszystkie aktywne sesje tego konta zostaną unieważnione.
        </Alert>
      </form>
    </Modal>
  )
}
